/**
 * Zod schemas for V4ActionExecution / ActionExecution JSON columns.
 *
 * Designed from the write-side producers (execution-state-machine.ts,
 * v4-action-executor.ts, execution-handlers/*) and verified against 115
 * production rows via `bun run schema-sample:production`.
 *
 * All readers of these columns should parse through these schemas rather
 * than assert with `as X` — the JSON columns carry no DB-level shape
 * guarantees.
 */

import { z } from 'zod';
import { parseDbJson, type DbJsonContext } from './db-json';

/**
 * Outcome of a completed action.
 * Writer: v4-action-executor.ts produces `{}` or `{ status: string }`.
 * Column default is `{}`, so `status` is optional.
 */
export const V4ExecutionOutcomeSchema = z.object({
  status: z.string().optional(),
});
export type V4ExecutionOutcome = z.infer<typeof V4ExecutionOutcomeSchema>;

/**
 * Meta block on HandlerResult.
 * This is the canonical declaration — execution-handlers/webhook.ts exports
 * `HandlerResultMeta` as an alias for `V4HandlerResultMeta` so writers and the
 * read-side `.parse()` share one type. Four declared shared fields + index
 * signature for handler-specific extras (recordUrl, httpStatus, stats, output, …).
 */
export const V4HandlerResultMetaSchema = z.looseObject({
  summary: z.string().optional(),
  links: z
    .array(
      z.object({
        label: z.string(),
        url: z.string(),
        icon: z.enum(['salesforce', 'external', 'slack']).optional(),
      }),
    )
    .optional(),
  details: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .optional(),
  durationMs: z.number().optional(),
  /**
   * The Gong-assigned flow-instance id (Gong Flow Play + Queue redesign,
   * U10) — set by `executeGongFlow` on a real dispatch, never omitted on success.
   * Declared here rather than left to `looseObject`'s passthrough (coding
   * standards: "extend the canonical schema, never smuggle a key through a
   * loose object") — Actions feed / Action View read this to identify the
   * Gong instance.
   */
  flowInstanceId: z.string().optional(),
});
export type V4HandlerResultMeta = z.infer<typeof V4HandlerResultMetaSchema>;

/**
 * handler_result column.
 * Canonical declaration — execution-handlers/webhook.ts exports `HandlerResult`
 * as an alias for `V4HandlerResult`. Writer: execution-state-machine.ts
 * markDelivered/markFailed.
 */
export const V4HandlerResultSchema = z.object({
  ok: z.boolean(),
  code: z.string(),
  message: z.string(),
  meta: V4HandlerResultMetaSchema.optional(),
});
export type V4HandlerResult = z.infer<typeof V4HandlerResultSchema>;

/**
 * delivery_error column.
 * Writer: execution-state-machine.ts:markFailed writes { code, message }
 * from HandlerResult. Readers in execution-derivation.ts also access
 * `attemptCount` and `lastAttemptAt` with fallback defaults — those fields
 * are not written today but are declared here to keep reader types sound.
 */
export const V4DeliveryErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  attemptCount: z.number().optional(),
  lastAttemptAt: z.string().optional(),
});
export type V4DeliveryError = z.infer<typeof V4DeliveryErrorSchema>;

/**
 * input / inputs / handler_payload columns.
 * Caller-supplied arbitrary objects — no project-wide shape.
 * Handlers read their own keys and are responsible for their own narrowing.
 */
export const V4ExecutionJsonObjectSchema = z.record(z.string(), z.unknown());
export type V4ExecutionJsonObject = z.infer<typeof V4ExecutionJsonObjectSchema>;

// Null-aware helpers — safe-parse + structured log on drift.
// All call sites must pass { column, rowId } so a malformed row names
// itself in the log. One drifted row must not 500 the endpoint.

/**
 * Parse an optional outcome. Returns `undefined` when the column is null,
 * the value is `{}` default, or the row drifted (drift is logged).
 */
export function parseOutcome(
  value: unknown,
  ctx: DbJsonContext,
): V4ExecutionOutcome | undefined {
  const parsed = parseDbJson(V4ExecutionOutcomeSchema, value, ctx);
  if (parsed === null) return undefined;
  return parsed.status === undefined ? undefined : parsed;
}

/**
 * Codes whose `message` was, in rows written before the write-side fix, the raw
 * text of a thrown error (a driver, axios, or SDK message may carry a host, a
 * query, or a token). Rows persist and are not backfilled, so the read
 * projection masks every row with these codes. New writes store only the fixed
 * message under them; a `PublicError`'s designed text is stored under
 * `HANDLER_REJECTED_CODE` instead, which passes through like any other designed
 * `{ ok: false }` result.
 */
const THROWN_ERROR_CODES: ReadonlySet<string> = new Set([
  'HANDLER_THREW',
  'SKILL_HANDLER_FAILED',
  'SKILL_EXECUTION_ERROR',
  'INTERNAL_SERVICE_THREW',
]);

export const THROWN_HANDLER_MESSAGE = 'The action failed unexpectedly.';

/** A handler threw a `PublicError`: its message is designed text and is stored and shown as-is. */
export const HANDLER_REJECTED_CODE = 'HANDLER_REJECTED';

function maskThrownMessage<T extends { code: string; message: string }>(value: T): T {
  return THROWN_ERROR_CODES.has(value.code) ? { ...value, message: THROWN_HANDLER_MESSAGE } : value;
}

export function parseHandlerResult(
  value: unknown,
  ctx: DbJsonContext,
): V4HandlerResult | null {
  const parsed = parseDbJson(V4HandlerResultSchema, value, ctx);
  return parsed && maskThrownMessage(parsed);
}

export function parseDeliveryError(
  value: unknown,
  ctx: DbJsonContext,
): V4DeliveryError | null {
  const parsed = parseDbJson(V4DeliveryErrorSchema, value, ctx);
  return parsed && maskThrownMessage(parsed);
}

export function parseExecutionJsonObject(
  value: unknown,
  ctx: DbJsonContext,
): V4ExecutionJsonObject | null {
  return parseDbJson(V4ExecutionJsonObjectSchema, value, ctx);
}
